import clsx from 'clsx';
import React from 'react';
import {Stack} from '@mui/material';
import {Body, Icon, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import Tag from '@/components/tag';
import styles from './AssetsHoverDetail.module.scss';
import {
  rollupDeliverables,
  type KindGroup,
  type KindRollup,
} from './assetsHoverRollup';

interface AssetsHoverDetailProps {
  creator: CampaignCreatorRow;
  campaignNetwork?: string | null;
}

function isTerminalGroup(group: KindGroup): boolean {
  return group.state === 'accepted' || group.state === 'posted';
}

function headerDateFor(row: KindRollup): string | null {
  const terminal = row.groups.find(
    (group) => isTerminalGroup(group) && group.dateSuffix
  );
  if (!terminal?.dateSuffix) {
    return null;
  }
  // Complete kinds already read as done; mixed kinds prefix so the date is
  // not confused with a due date on an open chip.
  if (row.isComplete) {
    return terminal.dateSuffix;
  }
  return translate(
    terminal.state === 'posted' ? 'Posted {date}' : 'Approved {date}',
    {date: terminal.dateSuffix}
  );
}

function isOpenGroup(group: KindGroup): boolean {
  // Header count covers approved/posted. Chips are only for work still open.
  return !isTerminalGroup(group);
}

function AssetsHoverDetail({creator, campaignNetwork}: AssetsHoverDetailProps) {
  const rows = rollupDeliverables(
    creator.deliverables,
    creator.contentDue,
    campaignNetwork,
    creator.minimumChildContentSubmissionCount
  );

  if (rows.length === 0) {
    return (
      <Stack className={styles.root} data-testid="assets-hover-detail">
        <Body size="sm">{translate('No deliverables yet')}</Body>
      </Stack>
    );
  }

  return (
    <Stack className={styles.root} data-testid="assets-hover-detail">
      {rows.map((row) => {
        const headerDate = headerDateFor(row);
        return (
          <Stack key={row.kind} className={styles.kind}>
            <Stack
              direction="row"
              className={clsx(styles.header, row.isComplete && styles.complete)}
            >
              <Icon
                className={styles.icon}
                name={row.icon}
                size="small"
                appearance="neutral"
                color={row.isComplete ? 'currentColor' : undefined}
              />
              <Body
                size="md"
                color={row.isComplete ? 'currentColor' : undefined}
              >
                {`${row.approvedCount}/${row.contractedCount} ${row.label}`}
                {row.frameCount != null ? (
                  <span className={styles.meta}>
                    {` (${translate('frame count', {count: row.frameCount})})`}
                  </span>
                ) : null}
                {headerDate ? (
                  <span className={styles.meta}>{` · ${headerDate}`}</span>
                ) : null}
              </Body>
            </Stack>
            {row.isComplete ? null : (
              <Stack className={styles.groups}>
                {row.groups.filter(isOpenGroup).map((group) => (
                  <Tag
                    key={`${group.state}-${group.round ?? 'none'}`}
                    label={
                      <Label size="sm" color="currentColor">
                        {group.summary}
                      </Label>
                    }
                    color={group.tagColor}
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

export default AssetsHoverDetail;
